import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Sunrise, Heart, Sparkles, LogOut, Home, User } from "lucide-react";

const iconMap = {
  Sunrise: Sunrise,
  Heart: Heart,
  Sparkles: Sparkles,
};

const Sanctuary = () => {
  const [sections, setSections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { token, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSections = async () => {
      try {
        const response = await axios.get(`${API}/sections`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSections(response.data);
      } catch (error) {
        console.error("Error fetching sections:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSections();
  }, [token]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const sectionImages = {
    "avant": "https://images.unsplash.com/photo-1526323876673-cf027f39c0d2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxhZXN0aGV0aWMlMjB0cm9waWNhbCUyMGxlYWYlMjBzaGFkb3clMjBvbiUyMGJlaWdlJTIwd2FsbHxlbnwwfHx8fDE3NzA0MzA4NTZ8MA&ixlib=rb-4.1.0&q=85",
    "pendant": "https://images.unsplash.com/photo-1767440398636-eb35f46bb342?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHx3b21hbiUyMG1lZGl0YXRpbmclMjB3aGl0ZSUyMGRyZXNzJTIwdHJvcGljYWwlMjBzYW5jdHVhcnl8ZW58MHx8fHwxNzcwNDMwODU1fDA&ixlib=rb-4.1.0&q=85",
    "apres": "https://images.unsplash.com/photo-1763930286396-51eb06f6f23a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGdvbGQlMjBmb2lsJTIwdGV4dHVyZSUyMG9yZ2FuaWMlMjBzaGFwZSUyMG9uJTIwd2hpdGV8ZW58MHx8fHwxNzcwNDMwODU3fDA&ixlib=rb-4.1.0&q=85"
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2] pb-24">
      {/* Header */}
      <header className="px-6 pt-8 pb-4">
        <div className="max-w-2xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="font-serif text-2xl text-[#3F2A24]">COCON SACRÉ</h1>
            <p className="text-xs tracking-[0.35em] uppercase font-medium text-[#8C6A4A] mt-1">TON SANCTUAIRE</p>
          </div>
          <button
            onClick={handleLogout}
            className="p-3 rounded-full hover:bg-[#C5A059]/10 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 text-[#4B3329]" strokeWidth={1.5} />
          </button>
        </div>
      </header>

      {/* Welcome Message */}
      <section className="px-6 py-8">
        <div className="max-w-2xl mx-auto text-center animate-fade-in-up">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#C5A059]/10 flex items-center justify-center animate-breathing">
            <Sparkles className="w-6 h-6 text-[#C5A059]" strokeWidth={1.5} />
          </div>
          <h2 className="font-serif text-3xl md:text-4xl text-[#3F2A24] mb-4">
            Bienvenue
          </h2>
          <p className="text-[#4B3329] leading-relaxed max-w-md mx-auto">
            Cet espace a été créé avec amour pour t'accompagner avant, pendant et après ta retraite.
          </p>
          <div className="divider-gold mt-6" />
        </div>
      </section>

      {/* Sections */}
      <section className="px-6 py-4">
        <div className="max-w-2xl mx-auto">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="skeleton h-48 rounded-2xl" />
              ))}
            </div>
          ) : sections.length === 0 ? (
            <div className="empty-state">
              <Sparkles className="empty-state-icon mx-auto" strokeWidth={1} />
              <p className="font-serif text-xl text-[#4B3329] mb-2">
                Ton cocon se prépare
              </p>
              <p className="text-[#8C6A4A]">
                Les contenus seront bientôt disponibles.
              </p>
            </div>
          ) : (
            <div className="space-y-4 stagger-children">
              {sections.map((section) => {
                const IconComponent = iconMap[section.icon] || Sparkles;
                return (
                  <Link
                    key={section.id}
                    to={`/section/${section.slug}`}
                    className="block card-sacred group relative overflow-hidden"
                    data-testid={`section-${section.slug}`}
                  >
                    {/* Background Image */}
                    <div 
                      className="absolute inset-0 opacity-20 group-hover:opacity-30 transition-opacity duration-500"
                      style={{
                        backgroundImage: `url(${sectionImages[section.slug] || ''})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }}
                    />
                    
                    {/* Content */}
                    <div className="relative z-10 p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-[#C5A059]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#C5A059]/20 transition-colors">
                          <IconComponent className="w-6 h-6 text-[#C5A059]" strokeWidth={1.5} />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-serif text-xl text-[#3F2A24] mb-2 group-hover:text-[#C5A059] transition-colors">
                            {section.name}
                          </h3>
                          <p className="text-sm text-[#4B3329] leading-relaxed">
                            {section.description}
                          </p>
                        </div>
                        <div className="w-8 h-8 rounded-full bg-[#C5A059]/0 group-hover:bg-[#C5A059]/10 flex items-center justify-center transition-all duration-300 group-hover:translate-x-1">
                          <svg className="w-4 h-4 text-[#C5A059]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Bottom Navigation */}
      <nav className="nav-sacred" data-testid="bottom-nav">
        <Link to="/sanctuary" className="nav-item active">
          <Home className="w-5 h-5" strokeWidth={1.5} />
          <span>Accueil</span>
        </Link>
        <button onClick={handleLogout} className="nav-item">
          <User className="w-5 h-5" strokeWidth={1.5} />
          <span>Sortir</span>
        </button>
      </nav>
    </div>
  );
};

export default Sanctuary;
